import { useRef, useState, useEffect } from 'react';
import { caseStudies } from '@/mocks/casesData';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { visible, ref };
}

export default function MengniuCase() {
  const data = caseStudies[0];
  const heroRef = useVisible(0.05);
  const challengeRef = useVisible(0.1);
  const solutionRef = useVisible(0.1);
  const resultsRef = useVisible(0.1);

  const scenarios = [
    {
      icon: 'ri-time-line',
      title: '大日期处理',
      desc: '依托物码大数据能力，系统智能识别产品效期差异，通过定制化差异扫码红包活动，高效消化存量库存，大幅减少临期产品积压损耗',
    },
    {
      icon: 'ri-stack-line',
      title: '囤货激励',
      desc: '精准激励经销商与终端门店主动备货，提升渠道铺货深度与广度',
    },
    {
      icon: 'ri-exchange-dollar-line',
      title: '动销分佣',
      desc: '按实际动销结果进行费用分配，让每一分营销资源精准直达终端，彻底杜绝渠道费用截留乱象',
    },
    {
      icon: 'ri-rocket-line',
      title: '新品推广',
      desc: '数字化赋能新品快速入市，精准触达核心终端与目标消费者，缩短新品动销爬坡周期',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Case Header with Banner */}
        <div ref={heroRef.ref} className={`mb-10 md:mb-14 transition-all duration-700 ${heroRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="relative rounded-2xl overflow-hidden h-48 md:h-72 mb-8">
            <img
              src={data.heroImage}
              alt={`${data.brand}案例背景`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-800/50 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">{data.brand}</h2>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed">{data.subtitle}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-text-muted">
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-surface rounded-lg">
              <i className="ri-building-line" /> 行业：{data.industry}
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-surface rounded-lg">
              <i className="ri-service-line" /> 服务：{data.service}
            </span>
          </div>
        </div>

        {/* Challenge */}
        <div ref={challengeRef.ref} className={`mb-10 md:mb-14 transition-all duration-500 ${challengeRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-lg">
              <i className="ri-error-warning-line text-white text-sm" />
            </span>
            核心挑战
          </h3>
          <div className="bg-surface rounded-xl p-5 md:p-7 border border-gray-100">
            <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-4">{data.challengeIntro}</p>
            <ul className="space-y-2.5">
              {data.challengePoints.map((item, i) => (
                <li key={i} className="text-sm text-text-secondary flex items-start gap-2.5">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Solution */}
        <div ref={solutionRef.ref} className={`mb-10 md:mb-14 transition-all duration-500 ${solutionRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-accent rounded-lg">
              <i className="ri-lightbulb-line text-primary text-sm" />
            </span>
            解决方案
          </h3>
          <div className="bg-primary rounded-xl p-5 md:p-7 text-white mb-6">
            <p className="text-sm md:text-base leading-relaxed mb-3">
              融慧数科以"<strong className="text-accent">促动销、管分销、提费效</strong>"为核心目标，围绕蒙牛全渠道营销场景，构建起一套可溯源、可管控、可持续优化的精细化数字化运营体系，重点落地以下四大核心场景：
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {scenarios.map((s, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-blue-50 rounded-lg mb-3 group-hover:bg-blue-600 transition-colors">
                  <i className={`${s.icon} text-blue-600 text-lg group-hover:text-white transition-colors`} />
                </div>
                <h4 className="text-sm font-semibold text-primary mb-2">{s.title}</h4>
                <p className="text-xs text-text-secondary leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <div ref={resultsRef.ref} className={`transition-all duration-500 ${resultsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-green-600 rounded-lg">
              <i className="ri-trophy-line text-white text-sm" />
            </span>
            落地成果
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.results.map((result, i) => (
              <div
                key={i}
                className="bg-surface rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-300 flex items-start gap-3"
              >
                <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-xs" />
                </div>
                <p className="text-sm text-text-secondary leading-relaxed">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}